"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { postWebBmbyLead } from "api"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/he"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { LegacyRef, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  extractProperty,
  extractPropertyMediaItems,
  getUmbracoMediaUrl,
  submitClick,
  submitError,
  submitSuccess,
} from "utils"
import { generatePortFormSchema } from "utils/Form/FormSchema"
import * as yup from "yup"
import NewText from "./NewText/NewText"
import PortContainer from "./PortContainer"
import PortFormResultState from "./PortFormResultState"
import { ArrowDownIcon, Check2Icon, LeftArrowIos, RightArrowIos } from "./icons"

const StyledSection = styled.section`
  --p-text: ${(props) => props.theme.colors.port.lightTextColor};
  display: flex;
  width: 100%;
  padding-block: 60px;
  color: var(--p-text);
  scroll-margin-top: 80px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-block: 90px;
    scroll-margin-top: 88px;
  }
`

const StyledPortWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 20px;
`

const StyledFormImageWrapper = styled.div`
  display: none;
  border-radius: 20px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 130px;
    display: flex;
    max-width: 508px;
    height: 948px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    max-width: 588px;
    height: 1045px;
  }
`

const StyledFormWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 402px;
  }
`

const StyledFormHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
  margin-bottom: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 40px;
  }
`
const StyledFormTitle = styled.h5`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  position: relative;
  z-index: 2;
  font-size: 30px;
  font-weight: 400;
  line-height: 125%; /* 37.5px */
  letter-spacing: -0.6px;
  text-transform: capitalize;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    letter-spacing: -0.8px;
  }
`

const StyledFormSubtitle = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${(props) => props.theme.breakpoints.up("xl")} {
    gap: 52px;
  }

  /* Remove autofill background color */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    -webkit-text-fill-color: #0e2846 !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  .MuiTextField-root,
  .MuiFormControl-root {
    display: flex;
    flex-direction: column;
  }

  /* Label styling - always on top */
  .MuiInputLabel-root {
    position: relative;
    transform: none;
    color: ${(props) => props.theme.colors.port.defaultTextColor};
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 8px;
    pointer-events: auto;

    &.Mui-focused {
      color: ${(props) => props.theme.colors.port.defaultTextColor};
    }

    &.MuiInputLabel-shrink {
      transform: none;
    }
  }

  /* Standard Input styling for TextField */
  .MuiInput-root {
    background-color: white;
    border: 1px solid #e4e4e4;
    border-radius: 8px;
    padding: 11px 16px;
    margin-top: 0;

    &:before,
    &:after {
      display: none;
    }

    &:hover {
      background-color: white;
    }

    &.Mui-focused {
      background-color: white;
      border-color: ${(props) => props.theme.colors.port.defaultTextColor};
    }

    &.Mui-error {
      border-color: #c92418;
    }

    .MuiInput-input {
      padding: 0;
      font-size: 16px;
    }
  }

  /* Select Input styling */
  .MuiInput-root.MuiInputBase-root.MuiInputBase-colorPrimary {
    &.MuiInputBase-formControl {
      margin-top: 0;
    }
  }

  .MuiSelect-select {
    background-color: white !important;
    padding: 0;
    font-size: 16px;
  }

  /* Select wrapper styling */
  .MuiInput-underline {
    &:before,
    &:after {
      display: none !important;
    }
  }
`

const StyledSelect = styled(Select)`
  background-color: white;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  height: 48px;

  &:before,
  &:after {
    display: none !important;
  }

  &:hover {
    background-color: white;
  }

  &.Mui-focused {
    background-color: white;
    border-color: #0e2846;
  }

  &.Mui-error {
    border-color: #c92418;
  }

  .MuiSelect-select {
    padding: 0 !important;
    background-color: transparent !important;
    height: auto !important;
    min-height: unset !important;
  }

  .MuiSelect-icon {
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
`

// Multiselect text display for closed state (comma-separated)
const MultiSelectText = styled.span`
  font-size: 16px;
  color: #0e2846;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-inline-end: 24px;
  width: 100%;
  display: block;
`

// Multiselect menu item styles
const MultiSelectMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  padding: 4px 8px !important;
  min-height: unset !important;
  gap: 12px;

  &:hover {
    background-color: transparent !important;
  }

  &.Mui-selected {
    background-color: transparent !important;
  }
`

// Blue checkbox inside the chip (only shown when selected)
const MultiSelectCheckbox = styled.span<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid;
  transition: all 0.2s ease-in-out;
  border-color: ${({ $isSelected, theme }) =>
    $isSelected ? "transparent" : theme.colors.port.grey};
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.propBlue : "transparent"};

  svg {
    width: 10px;
    height: 10px;
  }
`

const multiSelectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "white",
      border: "1px solid #e4e4e4",
      borderRadius: "8px",
      boxShadow: "0px 4px 12px rgba(42, 42, 42, 0.12)",
      marginTop: "4px",
      "& .MuiList-root": {
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      },
    },
  },
}

const selectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "white",
      border: "1px solid #e4e4e4",
      borderRadius: "8px",
      boxShadow: "0px 4px 12px rgba(42, 42, 42, 0.12)",
      marginTop: "4px",
      "& .MuiList-root": {
        padding: 0,
      },
      "& .MuiMenuItem-root": {
        padding: "11px 24px",
        fontFamily: "var(--simpler-pro-full) !important",
        fontSize: "16px",
        fontWeight: 700,
        color: "#2a2a2a",
        textTransform: "uppercase",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 250, 0.04)",
        },
        "&.Mui-focusVisible": {
          backgroundColor: "transparent",
        },
        "&.Mui-selected": {
          backgroundColor: "transparent",
          color: "#0000fa",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 250, 0.04)",
          },
        },
      },
    },
  },
}

const SelectWithArrow = (props: any) => (
  <StyledSelect {...props} IconComponent={ArrowDownIcon} MenuProps={selectMenuProps} />
)

const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-root {
    background-color: white;
    border: 1.2px solid #e6e6e6;
    border-radius: 12px;
    height: 48px;

    &:before,
    &:after {
      display: none !important;
    }

    &:hover {
      background-color: white;
    }

    &.Mui-focused {
      background-color: white;
      border-color: #0e2846;
    }

    &.Mui-error {
      border-color: #c92418;
    }

    .MuiInputAdornment-root {
      max-height: max-content;
      width: max-content;
      height: 100%;
    }
  }

  .MuiInputBase-input {
    padding: 12px 16px;
    font-size: 16px;

    &::placeholder {
      color: #687079;
      opacity: 1;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputAdornment-root {
    margin-left: 8px;
    margin-right: 0;
  }
`

// Custom styles for the DatePicker calendar popup
const datePickerSlotProps = {
  popper: {
    sx: {
      "& .MuiPaper-root": {
        backgroundColor: "white",
        border: "1.2px solid #e6e6e6",
        borderRadius: "8px",
        boxShadow: "0px 4px 12px rgba(42, 42, 42, 0.12)",

        height: "246px !important",
      },
      "& .MuiPickersCalendarHeader-root": {
        padding: "12px 16px",
        marginBottom: 0,
      },
      "& .MuiPickersCalendarHeader-label": {
        fontFamily: "var(--simpler-pro-full) !important",
        fontWeight: 700,
        fontSize: "18px",
        color: "#0e2846",
      },
      "& .MuiPickersArrowSwitcher-root": {
        gap: "4px",
      },
      "& .MuiPickersArrowSwitcher-button": {
        color: "#0e2846",
        padding: "6px",
        borderRadius: "38px",
        "& svg": {
          width: "16px",
          height: "16px",
        },
      },
      "& .MuiDayCalendar-header": {
        justifyContent: "space-between",
        padding: "0 8px",
        "& .MuiDayCalendar-weekDayLabel": {
          fontFamily: "var(--simpler-pro-full) !important",
          fontWeight: 700,
          fontSize: "18px",
          color: "#0e2846",
          width: "30px",
          height: "22px",
        },
      },
      "& .MuiDayCalendar-monthContainer": {
        padding: "0 16px 16px",
      },
      "& .MuiDayCalendar-weekContainer": {
        justifyContent: "space-between",
        gap: "14px",
      },
      "& .MuiPickersDay-root": {
        fontFamily: "var(--simpler-pro-full) !important",
        fontWeight: 400,
        fontSize: "16px",
        color: "#2a2a2a",
        width: "30px",
        height: "30px",
        margin: 0,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 250, 0.08)",
        },
        "&:focus": {
          backgroundColor: "#0000fa",
        },
        "&.Mui-selected": {
          backgroundColor: "#0000fa",
          color: "white",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "#0000fa",
          },
        },
        "&.MuiPickersDay-today": {
          border: "1px solid #2a2a2a",
          borderRadius: "50%",
          backgroundColor: "transparent",
          "&:not(.Mui-selected)": {
            border: "1px solid #2a2a2a",
          },
        },
        "&.MuiPickersDay-dayOutsideMonth": {
          color: "#748083",
        },
      },
      "& .MuiPickersSlideTransition-root": {
        minHeight: "178px",
      },
    },
  },
  textField: {
    placeholder: "בחר תאריך",
    fullWidth: true,
  },
}

const StyledDatePickerLabel = styled.label`
  color: #0e2846;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
`

const StyledFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  flex-direction: row;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
  }
`

const StyledFieldHalf = styled.div`
  flex: 1;
  width: 48%;
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  p {
    margin: 0;
  }
  a {
    color: ${(props) => props.theme.colors.port.defaultTextColor};
  }
`

const StyledDisclaimerLink = styled(Link)`
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  text-decoration: underline;
`

const StyledFormBottomWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  font-family: var(--simpler-pro-full) !important;

  & *,
  & *::before,
  & *::after {
    font-family: var(--simpler-pro-full) !important;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    gap: 8px;
  }
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 0;
  ${(props) =>
    !props.status
      ? `rect {
      stroke: #C92418;
    }`
      : ""};
`

// Styled checkbox icon similar to MultiSelectCheckbox
const FormCheckboxIcon = styled.span<{ $isChecked?: boolean; $hasError?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid;
  transition: all 0.2s ease-in-out;
  border-color: ${({ $isChecked, $hasError, theme }) =>
    $hasError ? "#C92418" : $isChecked ? "transparent" : theme.colors.port.grey};
  background-color: ${({ $isChecked, theme }) =>
    $isChecked ? theme.colors.propBlue : "transparent"};
  cursor: pointer;

  svg {
    width: 10px;
    height: 10px;
  }
`

const StyledHelperText = styled.span<{ isError?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.isError ? "#C92418" : "#008015")};
  margin-top: 4px;
  display: block;
`

const StyledCaldendarButton = styled.button`
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;

  background: none;
  border: none;
  cursor: pointer;
  padding: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 16px;
    height: 16px;
  }
`

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 16px;
  }
`

const StyledPhoneSection = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  font-size: 18px;
  font-weight: 700;
  line-height: 100%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const StyledSocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledSocialIconWrapper = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 27px;
  border: 1px solid white;
  background: rgba(255, 255, 255, 0.8);

  /* Drop Shadow */
  box-shadow: 0 0 17.3px 0 rgba(114, 111, 111, 0.2);

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 45px;
    height: 45px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

type PortFormProps = {
  data: any
  companyId?: number
  portNames?: string[]
  facilityTypes?: string[]
  mediaTitle?: string
}

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY

const PortForm = ({ data, companyId, mediaTitle }: PortFormProps) => {
  const locationOptions: string[] = extractProperty(data, "locationOptions") || []
  const interestedInOptions: string[] = extractProperty(data, "interestedInOptions") || []

  const title = extractProperty(data, "title")
  const subtitle = extractProperty(data, "subtitle")
  const desktopImage = extractProperty(data, "desktopImage")
  const anchorID = extractProperty(data, "anchorID")
  const bmbyID = extractProperty(data, "bmbyID")
  const buttonText = extractProperty(data, "buttonText") || ""
  const emailForLead = extractProperty(data, "emailForLead") || ""

  const socialItems = extractProperty(data, "socialItems") || []
  const socials =
    socialItems?.map((item: any) => ({
      link: extractProperty(item, "link")?.[0] || null,
      icon: extractPropertyMediaItems(item, "icon")?.[0],
    })) || []
  const contactLink = extractProperty(data, "contactLink")?.[0] || null
  const contactIcon = extractPropertyMediaItems(data, "contactIcon")?.[0]

  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()
  const sectionRef = useRef<HTMLElement>(null)
  const locale = useLocale()
  const { colors } = useTheme()
  const tPort = useTranslations("PortForm")

  // Scroll to section when form is submitted to ensure success/fail message is visible
  useEffect(() => {
    if (formIsSubmitted && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [formIsSubmitted])
  const schema = generatePortFormSchema(locale === "he" ? "he" : "en")

  type FormInput = yup.InferType<typeof schema>

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitted, isSubmitting, isValid },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: "",
      phone: "",
      companyName: "",
      email: "",
      interestedIn: [],
      numberOfPeople: "",
      location: "",
      preferredDate: undefined,
      allowMarketing: false,
      acceptTerms: false,
    },
    reValidateMode: "onChange",
    mode: "onSubmit",
  })

  const onSubmit: SubmitHandler<FormInput> = async (formData) => {
    // Ensure reCAPTCHA is available and get a valid response
    if (!reCaptchaRef?.current) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      submitError("שליחה", title || "טופס יצירת קשר", "reCAPTCHA is not available")
      return
    }

    reCaptchaRef.current.reset()
    let recaptchaResponse: string | null = null

    try {
      recaptchaResponse = await reCaptchaRef.current.executeAsync()
    } catch (recaptchaError) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      submitError("שליחה", title || "טופס יצירת קשר", "reCAPTCHA validation failed")
      return
    }

    if (!recaptchaResponse) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      submitError("שליחה", title || "טופס יצירת קשר", "reCAPTCHA response is empty")
      return
    }

    try {
      const formattedDate = formData.preferredDate
        ? dayjs(formData.preferredDate as Date).format("DD/MM/YYYY")
        : ""
      const interestedInText = Array.isArray(formData.interestedIn)
        ? formData.interestedIn.join(", ")
        : formData.interestedIn
      const locationText = formData.location ? String(formData.location).trim() : ""
      const comments = [
        formData.companyName ? `שם החברה: ${formData.companyName}` : "",
        `מתעניין ב: ${interestedInText}`,
        `מספר אנשים: ${formData.numberOfPeople}`,
        locationText ? `מיקום: ${locationText}` : "",
        `תאריך מועדף: ${formattedDate}`,
      ]
        .filter(Boolean)
        .join(" | ")

      const senddata = {
        projectId: bmbyID || -1,
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        comments,
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "",
        allowedMarketing: !!formData.allowMarketing,
        allowedSmsAndEmail: !!formData.allowMarketing,
        companyId: companyId || -1,
        uri: window?.location?.href,
        toEmail: emailForLead || "",
        intIn: interestedInText || "",
        mIkum: locationText || "",
        ...(mediaTitle ? { mediaTitle } : {}),
      }

      const response = await postWebBmbyLead(senddata)

      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", title || "טופס יצירת קשר", true)
        setSubmitState("success")
        setFormIsSubmitted(true)
        reset()
      } else {
        setSubmitState("fail")
        setFormIsSubmitted(true)
        submitError("שליחה", title || "טופס יצירת קשר", response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      reset()
      submitError("שליחה", title || "טופס יצירת קשר", error as any)
    }
  }

  const formHasInteracted = isSubmitted || isSubmitting

  const renderSuccessFailState = () => (
    <PortFormResultState
      isSuccess={submitState === "success"}
      title={submitState === "success" ? tPort("successTitle") : tPort("errorTitle")}
      subtitle={submitState === "success" ? tPort("successSubtitle") : tPort("errorSubtitle")}
      buttonText={submitState === "success" ? tPort("submitNewForm") : tPort("tryAgain")}
      onButtonClick={() => setFormIsSubmitted(false)}
    />
  )

  // in case data fields are empty, return null
  if (!title && !subtitle) {
    return null
  }

  return (
    <StyledSection ref={sectionRef} id={anchorID || "port-form"}>
      <PortContainer paddingInline={20}>
        <StyledPortWrapper>
          <StyledFormWrapper>
            {!formIsSubmitted && (
              <StyledFormHeader>
                {title && <StyledFormTitle>{title}</StyledFormTitle>}
                {subtitle && <StyledFormSubtitle>{subtitle}</StyledFormSubtitle>}
              </StyledFormHeader>
            )}

            {formIsSubmitted ? (
              renderSuccessFailState()
            ) : (
              <StyledForm onSubmit={handleSubmit(onSubmit)}>
                {/* Row 1: Full Name */}
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="*שם מלא"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.fullName}
                      helperText={
                        formHasInteracted && errors.fullName?.message ? (
                          <StyledHelperText isError>{errors.fullName.message}</StyledHelperText>
                        ) : null
                      }
                      fullWidth
                    />
                  )}
                />

                {/* Row 2: Phone + Company Name */}
                <StyledFieldRow>
                  <StyledFieldHalf>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="*מספר טלפון"
                          variant="standard"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.phone}
                          helperText={
                            formHasInteracted && errors.phone?.message ? (
                              <StyledHelperText isError>{errors.phone.message}</StyledHelperText>
                            ) : null
                          }
                          fullWidth
                        />
                      )}
                    />
                  </StyledFieldHalf>
                  <StyledFieldHalf>
                    <Controller
                      name="companyName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="שם החברה"
                          variant="standard"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </StyledFieldHalf>
                </StyledFieldRow>

                {/* Row 3: Email */}
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='*דוא"ל'
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.email}
                      helperText={
                        formHasInteracted && errors.email?.message ? (
                          <StyledHelperText isError>{errors.email.message}</StyledHelperText>
                        ) : null
                      }
                      fullWidth
                    />
                  )}
                />

                {/* Row 4: Interested In + Number of People */}
                <StyledFieldRow>
                  <StyledFieldHalf>
                    <FormControl fullWidth variant="standard" error={!!errors.interestedIn}>
                      <InputLabel shrink>*מתעניין ב</InputLabel>
                      <Controller
                        name="interestedIn"
                        control={control}
                        render={({ field }) => (
                          <StyledSelect
                            {...field}
                            multiple
                            value={field.value || []}
                            IconComponent={ArrowDownIcon}
                            MenuProps={multiSelectMenuProps}
                            renderValue={(selected: unknown) => {
                              const selectedArray = selected as string[]
                              if (selectedArray.length === 0) return ""
                              const displayText = selectedArray.join(", ")
                              return <MultiSelectText>{displayText}...</MultiSelectText>
                            }}
                          >
                            {interestedInOptions?.map((type) => {
                              const isSelected = (field.value as string[])?.includes(type)
                              return (
                                <MultiSelectMenuItem key={type} value={type}>
                                  <MultiSelectCheckbox $isSelected={isSelected}>
                                    {isSelected && <Check2Icon />}
                                  </MultiSelectCheckbox>
                                  {type}
                                </MultiSelectMenuItem>
                              )
                            })}
                          </StyledSelect>
                        )}
                      />
                      {formHasInteracted && errors.interestedIn?.message && (
                        <StyledHelperText isError>
                          {errors.interestedIn.message as string}
                        </StyledHelperText>
                      )}
                    </FormControl>
                  </StyledFieldHalf>
                  <StyledFieldHalf>
                    <Controller
                      name="numberOfPeople"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="מספר אנשים"
                          variant="standard"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.numberOfPeople}
                          helperText={
                            formHasInteracted && errors.numberOfPeople?.message ? (
                              <StyledHelperText isError>
                                {errors.numberOfPeople.message}
                              </StyledHelperText>
                            ) : null
                          }
                          fullWidth
                        />
                      )}
                    />
                  </StyledFieldHalf>
                </StyledFieldRow>

                {/* Row 5: Location + Preferred Date */}
                <StyledFieldRow>
                  <StyledFieldHalf>
                    <FormControl fullWidth variant="standard" error={!!errors.location}>
                      <InputLabel shrink>*מיקום</InputLabel>
                      <Controller
                        name="location"
                        control={control}
                        render={({ field }) => (
                          <SelectWithArrow {...field}>
                            {locationOptions?.map((name) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                          </SelectWithArrow>
                        )}
                      />
                      {formHasInteracted && errors.location?.message && (
                        <StyledHelperText isError>{errors.location.message}</StyledHelperText>
                      )}
                    </FormControl>
                  </StyledFieldHalf>
                  <StyledFieldHalf>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <StyledDatePickerLabel>*באיזה תאריך</StyledDatePickerLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                        <Controller
                          name="preferredDate"
                          control={control}
                          render={({ field }) => (
                            <StyledDatePicker
                              value={field.value ? dayjs(field.value as Date) : null}
                              onChange={(date) => {
                                field.onChange(date ? (date as Dayjs).toDate() : null)
                              }}
                              minDate={dayjs()}
                              format="DD/MM/YYYY"
                              slots={{
                                openPickerIcon: ArrowDownIcon,
                                calendarHeader: (props: any) => {
                                  const { currentMonth, onMonthChange } = props
                                  const monthLabel = currentMonth.locale("he").format("MMMM YYYY")
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "12px 16px",
                                      }}
                                    >
                                      <StyledCaldendarButton
                                        type="button"
                                        aria-label={tPort("previous")}
                                        onClick={() =>
                                          onMonthChange(currentMonth.subtract(1, "month"), "right")
                                        }
                                      >
                                        <RightArrowIos color="#0e2846" />
                                        {tPort("previous")}
                                      </StyledCaldendarButton>
                                      <span
                                        style={{
                                          fontFamily: "var(--simpler-pro-full) !important",
                                          fontWeight: 700,
                                          fontSize: 18,
                                          color: "#0e2846",
                                        }}
                                      >
                                        {monthLabel}
                                      </span>
                                      <StyledCaldendarButton
                                        type="button"
                                        aria-label={tPort("next")}
                                        onClick={() =>
                                          onMonthChange(currentMonth.add(1, "month"), "left")
                                        }
                                      >
                                        {tPort("next")}
                                        <LeftArrowIos color="#0e2846" />
                                      </StyledCaldendarButton>
                                    </div>
                                  )
                                },
                              }}
                              slotProps={{
                                ...datePickerSlotProps,
                                textField: {
                                  ...datePickerSlotProps.textField,
                                  error: !!errors.preferredDate,
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {formHasInteracted && errors.preferredDate?.message && (
                        <StyledHelperText isError>
                          {errors.preferredDate.message as string}
                        </StyledHelperText>
                      )}
                    </div>
                  </StyledFieldHalf>
                </StyledFieldRow>
                <StyledFormBottomWrapper>
                  {/* Checkboxes */}
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer>
                      <Controller
                        name="allowMarketing"
                        control={control}
                        render={({ field }) => (
                          <StyledCheckbox
                            icon={<FormCheckboxIcon />}
                            checkedIcon={
                              <FormCheckboxIcon $isChecked>
                                <Check2Icon />
                              </FormCheckboxIcon>
                            }
                            sx={{ ":hover": { backgroundColor: "transparent" } }}
                            status
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight={300}
                          desktopLineHeight="150%"
                          component="div"
                          color="black"
                        >
                          {
                            "מקובל עליי לקבל מקבוצת אשטרום בע\"מ ו/או חברות הבת, פניות שיווקיות והכל, בהתאם "
                          }
                          <StyledDisclaimerLink href="/terms-of-use" target="_blank">
                            למדיניות הפרטיות
                          </StyledDisclaimerLink>
                          {", אין בהסכמתי משום הסכמה לביצוע עסקה"}
                        </NewText>
                      </div>
                    </StyledCheckboxContainer>

                    <StyledCheckboxContainer>
                      <Controller
                        name="acceptTerms"
                        control={control}
                        render={({ field }) => (
                          <StyledCheckbox
                            icon={<FormCheckboxIcon />}
                            checkedIcon={
                              <FormCheckboxIcon $isChecked>
                                <Check2Icon />
                              </FormCheckboxIcon>
                            }
                            sx={{ ":hover": { backgroundColor: "transparent" } }}
                            status
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight={300}
                          desktopLineHeight="150%"
                          component="div"
                          color="black"
                        >
                          {
                            "מקובל עליי לקבל מקבוצת אשטרום בע\"מ ו/או חברות הבת ו/או מי מטעמן תוכן פרסומי באמצעים אלקטרוניים כמו SMS, דוא\"ל, חיוג אוטומטי, ואטסאפ ועוד והכל, בהתאם "
                          }
                          <StyledDisclaimerLink href="/privacy-policy" target="_blank">
                            למדיניות הפרטיות
                          </StyledDisclaimerLink>
                          .
                        </NewText>
                      </div>
                    </StyledCheckboxContainer>

                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight={300}
                      desktopLineHeight="150%"
                      component="p"
                      color="black"
                    >
                      {"בשליחת טופס זה אני מאשר/ת שקראתי את "}
                      <StyledDisclaimerLink href="/privacy-policy" target="_blank">
                        מדיניות הפרטיות
                      </StyledDisclaimerLink>
                      {"של קבוצת אשטרום בע\"מ. לרבות של החברות הבנות"}
                    </NewText>
                  </CheckBoxesContainer>
                  <Button
                    variant="contained"
                    sx={{
                      marginTop: {
                        xl: "24px",
                      },
                      fontSize: {
                        xs: "18px",
                      },
                      minWidth: {
                        xs: "103px",
                        md: "200px",
                      },
                      justifySelf: "center",
                      height: {
                        xs: "48px",
                      },
                      "&:disabled": {
                        backgroundColor: colors.port.portBulletColor,
                      },
                    }}
                    type="submit"
                    size="large"
                    disabled={isSubmitting || (!isValid && formHasInteracted)}
                    onClick={() => {
                      submitClick("שליחה", title || "טופס יצירת קשר")
                      if (Object.keys(errors).length > 0 || !isValid) {
                        submitError(
                          "שליחה",
                          title || "טופס יצירת קשר",
                          errors?.fullName?.message ||
                            errors?.email?.message ||
                            errors?.phone?.message ||
                            "Fields are mandatory",
                        )
                      }
                    }}
                  >
                    {isSubmitting ? "שולח..." : buttonText}
                  </Button>
                </StyledFormBottomWrapper>
              </StyledForm>
            )}
            <StyledFooter>
              {contactLink?.url && (
                <StyledPhoneSection href={contactLink.url} aria-label="phone number">
                  {contactIcon && (
                    <Image
                      src={getUmbracoMediaUrl(contactIcon)}
                      width={20}
                      height={20}
                      alt="Contact Icon"
                    />
                  )}
                  {contactLink?.name && <span>{contactLink.name}</span>}
                </StyledPhoneSection>
              )}
              <StyledSocialIcons>
                {socials.map(
                  (
                    {
                      icon,
                      link,
                    }: {
                      icon: string | null
                      link: {
                        url: string
                        target: string
                      } | null
                    },
                    index: number,
                  ) => (
                    <StyledSocialIconWrapper
                      key={index}
                      href={link?.url}
                      target={link?.target || "_blank"}
                      rel="noopener noreferrer"
                      aria-label={`Social media link ${index + 1}`}
                    >
                      {icon && (
                        <Image
                          src={getUmbracoMediaUrl(icon)}
                          width={20}
                          height={20}
                          alt={`Social media icon`}
                        />
                      )}
                    </StyledSocialIconWrapper>
                  ),
                )}
              </StyledSocialIcons>
            </StyledFooter>
            {RECAPTCHA_KEY && (
              <ReCAPTCHA
                ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                sitekey={RECAPTCHA_KEY}
                size="invisible"
                hl="iw"
                tabIndex={-1}
              />
            )}
          </StyledFormWrapper>
          <StyledFormImageWrapper>
            {desktopImage && (
              <Image
                src={getUmbracoMediaUrl(desktopImage)}
                width={588}
                height={1045}
                alt="Desktop Form Image"
              />
            )}
          </StyledFormImageWrapper>
        </StyledPortWrapper>
      </PortContainer>
    </StyledSection>
  )
}
export default PortForm
